import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Button,
  Modal,
  Space,
  message,
  Card,
  Typography,
  Tooltip,
  Tag,
  Row,
  Col,
  Statistic,
  Select,
  DatePicker,
  Input,
  Descriptions,
  Divider,
  Empty,
  Avatar,
  Popconfirm,
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  EyeOutlined,
  FileOutlined,
  FileImageOutlined,
  VideoCameraOutlined,
  SoundOutlined,
  FileTextOutlined,
  FilterOutlined,
  ClearOutlined,
  DeleteOutlined,
  ClockCircleOutlined,
  UserOutlined,
  DatabaseOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import PagesWrapper from '../../../PagesWrapper';
import filesService from '../../../services/filesService';
import { getProfileImageUrl } from '../../../utils/urlUtils';
import styles from './DeletedFiles.module.css';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

/**
 * Category icons mapping
 */
const CategoryIcons = {
  Image: <FileImageOutlined />,
  Video: <VideoCameraOutlined />,
  Audio: <SoundOutlined />,
  Document: <FileTextOutlined />,
  Other: <FileOutlined />,
};

/**
 * Deleted Files Management Page
 * Admin page for viewing file deletion audit logs
 */
const DeletedFiles = () => {
  const { t } = useTranslation();
  
  // Data state
  const [deletionLogs, setDeletionLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statistics, setStatistics] = useState({
    totalDeleted: 0,
    totalSizeBytes: 0,
    byCategory: {},
  });
  
  // Pagination
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  
  // Filters
  const [filters, setFilters] = useState({
    category: null,
    dateRange: null,
    searchTerm: '',
  });
  const [showFilters, setShowFilters] = useState(false);
  
  // Modals
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);

  // Selection for bulk operations
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [deleteLoading, setDeleteLoading] = useState(false);

  /**
   * Fetch deletion logs from API
   */
  const fetchDeletionLogs = useCallback(async (page = 1, pageSize = 20) => {
    setLoading(true);
    try {
      const params = {
        page,
        pageSize,
      };
      
      if (filters.category) params.category = filters.category;
      if (filters.dateRange && filters.dateRange[0]) {
        params.fromDate = filters.dateRange[0].toISOString();
      }
      if (filters.dateRange && filters.dateRange[1]) {
        params.toDate = filters.dateRange[1].toISOString();
      }
      if (filters.searchTerm) {
        params.searchTerm = filters.searchTerm;
      }

      const response = await filesService.getDeletionLogs(params);
      setDeletionLogs(response.items || []);
      setPagination({
        current: response.page,
        pageSize: response.pageSize,
        total: response.totalCount,
      });
    } catch (error) {
      message.error(t('admin.deletedFiles.fetchError', 'Failed to load deletion logs'));
      console.error('Failed to fetch deletion logs:', error);
    } finally {
      setLoading(false);
    }
  }, [filters, t]);

  /**
   * Calculate statistics from logs
   */
  const calculateStatistics = useCallback(async () => {
    setStatsLoading(true);
    try {
      // Fetch all logs for statistics (or use a dedicated endpoint if available)
      const response = await filesService.getDeletionLogs({ page: 1, pageSize: 1000 });
      const logs = response.items || [];
      
      const stats = {
        totalDeleted: response.totalCount || logs.length,
        totalSizeBytes: logs.reduce((sum, log) => sum + (log.fileSizeBytes || 0), 0),
        byCategory: {},
      };
      
      logs.forEach(log => {
        const category = log.category || 'Other';
        stats.byCategory[category] = (stats.byCategory[category] || 0) + 1;
      });
      
      setStatistics(stats);
    } catch (error) {
      console.error('Failed to calculate statistics:', error);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDeletionLogs(pagination.current, pagination.pageSize);
    calculateStatistics();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Handle table pagination changes
   */
  const handleTableChange = (newPagination) => {
    fetchDeletionLogs(newPagination.current, newPagination.pageSize);
  };

  /**
   * Apply filters
   */
  const applyFilters = () => {
    fetchDeletionLogs(1, pagination.pageSize);
  };

  /**
   * Reset filters
   */
  const resetFilters = () => {
    setFilters({
      category: null,
      dateRange: null,
      searchTerm: '',
    });
    setTimeout(() => fetchDeletionLogs(1, pagination.pageSize), 0);
  };

  /**
   * View log detail
   */
  const viewLogDetail = (log) => {
    setSelectedLog(log);
    setDetailModalVisible(true);
  };

  /**
   * Delete a single deletion log
   */
  const handleDeleteLog = async (logId) => {
    try {
      setDeleteLoading(true);
      await filesService.deleteDeletionLog(logId);
      message.success(t('admin.deletedFiles.deleteSuccess', 'Deletion log removed successfully'));
      fetchDeletionLogs(pagination.current, pagination.pageSize);
      calculateStatistics();
      setSelectedRowKeys(prev => prev.filter(key => key !== logId));
    } catch (error) {
      message.error(t('admin.deletedFiles.deleteError', 'Failed to delete log'));
      console.error('Failed to delete deletion log:', error);
    } finally {
      setDeleteLoading(false);
    }
  };

  /**
   * Bulk delete selected deletion logs
   */
  const handleBulkDelete = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning(t('admin.deletedFiles.noSelection', 'Please select logs to delete'));
      return;
    }

    try {
      setDeleteLoading(true);
      const result = await filesService.bulkDeleteDeletionLogs(selectedRowKeys);
      message.success(t('admin.deletedFiles.bulkDeleteSuccess', '{{count}} deletion log(s) removed successfully', { count: result.deletedCount }));
      setSelectedRowKeys([]);
      fetchDeletionLogs(pagination.current, pagination.pageSize);
      calculateStatistics();
    } catch (error) {
      message.error(t('admin.deletedFiles.bulkDeleteError', 'Failed to delete selected logs'));
      console.error('Failed to bulk delete deletion logs:', error);
    } finally {
      setDeleteLoading(false);
    }
  };

  /**
   * Row selection configuration
   */
  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
    getCheckboxProps: (record) => ({
      disabled: deleteLoading,
    }),
  };

  /**
   * Get category style class
   */
  const getCategoryClass = (category) => {
    switch (category) {
      case 'Image': return styles.categoryImage;
      case 'Video': return styles.categoryVideo;
      case 'Audio': return styles.categoryAudio;
      case 'Document': return styles.categoryDocument;
      default: return styles.categoryOther;
    }
  };

  /**
   * Format timestamp
   */
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleString();
  };

  /**
   * Get relative time
   */
  const getRelativeTime = (timestamp) => {
    if (!timestamp) return '';
    const now = new Date();
    const date = new Date(timestamp);
    const diffMs = now - date;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);

    if (diffSec < 60) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHour < 24) return `${diffHour}h ago`;
    if (diffDay < 7) return `${diffDay}d ago`;
    return formatTimestamp(timestamp);
  };

  /**
   * Table columns configuration
   */
  const columns = [
    {
      title: t('admin.deletedFiles.columns.category', 'Type'),
      dataIndex: 'category',
      key: 'category',
      width: 100,
      render: (category) => (
        <span className={`${styles.categoryBadge} ${getCategoryClass(category)}`}>
          {CategoryIcons[category] || CategoryIcons.Other}
          <span style={{ marginLeft: 4 }}>{category}</span>
        </span>
      ),
      filters: [
        { text: 'Image', value: 'Image' },
        { text: 'Video', value: 'Video' },
        { text: 'Audio', value: 'Audio' },
        { text: 'Document', value: 'Document' },
        { text: 'Other', value: 'Other' },
      ],
      onFilter: (value, record) => record.category === value,
    },
    {
      title: t('admin.deletedFiles.columns.fileName', 'File Name'),
      dataIndex: 'fileName',
      key: 'fileName',
      width: 600,
      ellipsis: true,
      render: (fileName, record) => (
        <div className={styles.fileNameCell}>
          <Tooltip title={fileName}>
            <span className={styles.originalFileName}>{fileName}</span>
          </Tooltip>
          {record.storedFileName && (
            <div className={styles.storageFileName}>{record.storedFileName}</div>
          )}
        </div>
      ),
    },
    {
      title: t('admin.deletedFiles.columns.size', 'Size'),
      dataIndex: 'fileSizeFormatted',
      key: 'size',
      width: 100,
      sorter: (a, b) => a.fileSizeBytes - b.fileSizeBytes,
    },
    {
      title: t('admin.deletedFiles.columns.deletedAt', 'Deleted'),
      dataIndex: 'dateDeleted',
      key: 'dateDeleted',
      width: 150,
      render: (date) => (
        <Tooltip title={formatTimestamp(date)}>
          <span>{getRelativeTime(date)}</span>
        </Tooltip>
      ),
      sorter: (a, b) => new Date(a.dateDeleted) - new Date(b.dateDeleted),
      defaultSortOrder: 'descend',
    },
    {
      title: t('admin.deletedFiles.columns.deletedBy', 'Deleted By'),
      dataIndex: 'deletedBy',
      key: 'deletedBy',
      width: 150,
      render: (deletedBy) => deletedBy ? (
        <Space>
          <Avatar 
            size="small" 
            src={getProfileImageUrl(deletedBy)}
            icon={<UserOutlined />} 
          />
          <span>{deletedBy.displayName || deletedBy.email}</span>
        </Space>
      ) : (
        <Text type="secondary">System</Text>
      ),
    },
    {
      title: t('admin.deletedFiles.columns.reason', 'Reason'),
      dataIndex: 'deletionReason',
      key: 'reason',
      width: 150,
      ellipsis: true,
      render: (reason) => reason || <Text type="secondary">-</Text>,
    },
    {
      title: t('admin.deletedFiles.columns.actions', 'Actions'),
      key: 'actions',
      width: 120,
      align: 'center',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title={t('common.view', 'View Details')}>
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => viewLogDetail(record)}
            />
          </Tooltip>
          <Popconfirm
            title={t('admin.deletedFiles.deleteConfirm.title', 'Delete this log?')}
            description={t('admin.deletedFiles.deleteConfirm.description', 'This action cannot be undone.')}
            onConfirm={() => handleDeleteLog(record.id)}
            okText={t('common.delete', 'Delete')}
            cancelText={t('common.cancel', 'Cancel')}
            okButtonProps={{ danger: true }}
          >
            <Tooltip title={t('common.delete', 'Delete')}>
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                loading={deleteLoading}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  /**
   * Render statistics cards
   */
  const renderStatistics = () => (
    <Row gutter={[16, 16]} className={styles.statsRow}>
      <Col xs={12} sm={8} md={6}>
        <Card className={styles.statCard} loading={statsLoading}>
          <Statistic
            title={t('admin.deletedFiles.stats.totalDeleted', 'Total Deleted')}
            value={statistics.totalDeleted}
            prefix={<DeleteOutlined style={{ color: '#ff7a45' }} />}
          />
        </Card>
      </Col>
      <Col xs={12} sm={8} md={6}>
        <Card className={styles.statCard} loading={statsLoading}>
          <Statistic
            title={t('admin.deletedFiles.stats.totalSize', 'Total Size')}
            value={filesService.formatFileSize(statistics.totalSizeBytes)}
            prefix={<DatabaseOutlined style={{ color: '#1890ff' }} />}
          />
        </Card>
      </Col>
      <Col xs={12} sm={8} md={6}>
        <Card className={styles.statCard} loading={statsLoading}>
          <Statistic
            title={t('admin.deletedFiles.stats.images', 'Images')}
            value={statistics.byCategory.Image || 0}
            prefix={<FileImageOutlined style={{ color: '#1890ff' }} />}
          />
        </Card>
      </Col>
      <Col xs={12} sm={8} md={6}>
        <Card className={styles.statCard} loading={statsLoading}>
          <Statistic
            title={t('admin.deletedFiles.stats.documents', 'Documents')}
            value={statistics.byCategory.Document || 0}
            prefix={<FileTextOutlined style={{ color: '#fa8c16' }} />}
          />
        </Card>
      </Col>
    </Row>
  );

  /**
   * Render filter panel
   */
  const renderFilterPanel = () => (
    <Card className={styles.filterPanel} size="small">
      <Row gutter={[16, 16]} align="bottom">
        <Col xs={24} sm={12} md={6}>
          <div className={styles.filterLabel}>
            {t('admin.deletedFiles.filters.category', 'Category')}
          </div>
          <Select
            style={{ width: '100%' }}
            placeholder={t('admin.deletedFiles.filters.allCategories', 'All Categories')}
            allowClear
            value={filters.category}
            onChange={(value) => setFilters({ ...filters, category: value })}
          >
            <Select.Option value="Image">
              <Space>{CategoryIcons.Image} Image</Space>
            </Select.Option>
            <Select.Option value="Video">
              <Space>{CategoryIcons.Video} Video</Space>
            </Select.Option>
            <Select.Option value="Audio">
              <Space>{CategoryIcons.Audio} Audio</Space>
            </Select.Option>
            <Select.Option value="Document">
              <Space>{CategoryIcons.Document} Document</Space>
            </Select.Option>
            <Select.Option value="Other">
              <Space>{CategoryIcons.Other} Other</Space>
            </Select.Option>
          </Select>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <div className={styles.filterLabel}>
            {t('admin.deletedFiles.filters.dateRange', 'Date Range')}
          </div>
          <RangePicker
            style={{ width: '100%' }}
            value={filters.dateRange}
            onChange={(dates) => setFilters({ ...filters, dateRange: dates })}
          />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <div className={styles.filterLabel}>
            {t('admin.deletedFiles.filters.search', 'Search')}
          </div>
          <Input
            placeholder={t('admin.deletedFiles.filters.searchPlaceholder', 'Search by filename...')}
            value={filters.searchTerm}
            onChange={(e) => setFilters({ ...filters, searchTerm: e.target.value })}
            allowClear
          />
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Space>
            <Button type="primary" onClick={applyFilters} icon={<SearchOutlined />}>
              {t('common.apply', 'Apply')}
            </Button>
            <Button onClick={resetFilters} icon={<ClearOutlined />}>
              {t('common.reset', 'Reset')}
            </Button>
          </Space>
        </Col>
      </Row>
      
      <Divider style={{ margin: '12px 0' }} />
      
      <div className={styles.timeRangeButtons}>
        <Text type="secondary" style={{ marginRight: 8 }}>
          {t('admin.deletedFiles.filters.quickRange', 'Quick Range:')}
        </Text>
        <Button size="small" onClick={() => setFilters({ ...filters, dateRange: [dayjs().subtract(24, 'hours'), dayjs()] })}>
          Last 24 Hours
        </Button>
        <Button size="small" onClick={() => setFilters({ ...filters, dateRange: [dayjs().subtract(7, 'days'), dayjs()] })}>
          Last 7 Days
        </Button>
        <Button size="small" onClick={() => setFilters({ ...filters, dateRange: [dayjs().subtract(30, 'days'), dayjs()] })}>
          Last 30 Days
        </Button>
      </div>
    </Card>
  );

  /**
   * Render detail modal
   */
  const renderDetailModal = () => (
    <Modal
      title={
        <Space>
          <DeleteOutlined style={{ color: '#ff7a45' }} />
          {t('admin.deletedFiles.detail.title', 'Deleted File Details')}
        </Space>
      }
      open={detailModalVisible}
      onCancel={() => {
        setDetailModalVisible(false);
        setSelectedLog(null);
      }}
      footer={[
        <Button key="close" onClick={() => setDetailModalVisible(false)}>
          {t('common.close', 'Close')}
        </Button>,
      ]}
      width={700}
    >
      {selectedLog && (
        <div>
          {/* File Preview Section */}
          <div className={styles.filePreview}>
            <div className={styles.thumbnailPlaceholder}>
              {React.cloneElement(CategoryIcons[selectedLog.category] || CategoryIcons.Other, {
                style: { fontSize: 32, color: '#8c8c8c' },
              })}
            </div>
            <div className={styles.fileInfo}>
              <div className={styles.fileName}>{selectedLog.fileName}</div>
              <div className={styles.fileMeta}>
                <Space split={<Divider type="vertical" />}>
                  <span>{selectedLog.fileSizeFormatted}</span>
                  <span>{selectedLog.contentType}</span>
                  <span className={`${styles.categoryBadge} ${getCategoryClass(selectedLog.category)}`}>
                    {selectedLog.category}
                  </span>
                </Space>
              </div>
            </div>
          </div>

          <Descriptions column={2} bordered size="small">
            <Descriptions.Item label={t('admin.deletedFiles.detail.originalId', 'Original File ID')}>
              {selectedLog.originalFileEntityId}
            </Descriptions.Item>
            <Descriptions.Item label={t('admin.deletedFiles.detail.logId', 'Log ID')}>
              {selectedLog.id}
            </Descriptions.Item>
            <Descriptions.Item label={t('admin.deletedFiles.detail.storedFileName', 'Stored File Name')} span={2}>
              <code>{selectedLog.storedFileName}</code>
            </Descriptions.Item>
            <Descriptions.Item label={t('admin.deletedFiles.detail.contentType', 'Content Type')}>
              {selectedLog.contentType}
            </Descriptions.Item>
            <Descriptions.Item label={t('admin.deletedFiles.detail.size', 'Size')}>
              {selectedLog.fileSizeFormatted} ({selectedLog.fileSizeBytes?.toLocaleString()} bytes)
            </Descriptions.Item>
            
            {(selectedLog.width || selectedLog.height) && (
              <Descriptions.Item label={t('admin.deletedFiles.detail.dimensions', 'Dimensions')} span={2}>
                {selectedLog.width} × {selectedLog.height} pixels
              </Descriptions.Item>
            )}
            
            <Descriptions.Item label={t('admin.deletedFiles.detail.hash', 'SHA256 Hash')} span={2}>
              <div className={styles.hashBox}>{selectedLog.hash}</div>
            </Descriptions.Item>
          </Descriptions>

          <Divider orientation="left">
            <ClockCircleOutlined /> {t('admin.deletedFiles.detail.timeline', 'Timeline')}
          </Divider>

          <Descriptions column={2} bordered size="small">
            <Descriptions.Item label={t('admin.deletedFiles.detail.originalCreated', 'Originally Uploaded')}>
              {formatTimestamp(selectedLog.originalDateCreated)}
            </Descriptions.Item>
            <Descriptions.Item label={t('admin.deletedFiles.detail.deletedAt', 'Deleted At')}>
              {formatTimestamp(selectedLog.dateDeleted)}
            </Descriptions.Item>
            <Descriptions.Item label={t('admin.deletedFiles.detail.uploadedBy', 'Uploaded By')}>
              {selectedLog.originalUploadedByUserId || <Text type="secondary">Unknown</Text>}
            </Descriptions.Item>
            <Descriptions.Item label={t('admin.deletedFiles.detail.deletedBy', 'Deleted By')}>
              {selectedLog.deletedBy ? (
                <Space>
                  <Avatar size="small" 
                    icon={<UserOutlined />} 
                    src={getProfileImageUrl(selectedLog.deletedBy)}
                  />
                  <span>{selectedLog.deletedBy.displayName || selectedLog.deletedBy.email}</span>
                </Space>
              ) : (
                <Text type="secondary">System</Text>
              )}
            </Descriptions.Item>
          </Descriptions>

          {(selectedLog.deletionReason || selectedLog.associatedEntityType) && (
            <>
              <Divider orientation="left">
                {t('admin.deletedFiles.detail.context', 'Deletion Context')}
              </Divider>
              <Descriptions column={1} bordered size="small">
                {selectedLog.deletionReason && (
                  <Descriptions.Item label={t('admin.deletedFiles.detail.reason', 'Reason')}>
                    {selectedLog.deletionReason}
                  </Descriptions.Item>
                )}
                {selectedLog.associatedEntityType && (
                  <Descriptions.Item label={t('admin.deletedFiles.detail.associatedEntity', 'Associated Entity')}>
                    <Tag color="blue">{selectedLog.associatedEntityType}</Tag>
                    {selectedLog.associatedEntityId && (
                      <span style={{ marginLeft: 8 }}>ID: {selectedLog.associatedEntityId}</span>
                    )}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </>
          )}
        </div>
      )}
    </Modal>
  );

  return (
    <PagesWrapper>
      <div className={styles.container}>
          <div className={styles.header}>
            <div className={styles.titleSection}>
              <DeleteOutlined className={styles.titleIcon} />
              <Title level={2} className={styles.title}>
                {t('admin.deletedFiles.title', 'Deleted Files')}
              </Title>
            </div>
            <Text type="secondary">
              {t('admin.deletedFiles.description', 'View audit logs for all deleted files in the system')}
            </Text>
          </div>

          {/* Statistics */}
          {renderStatistics()}

          {/* Toolbar */}
          <div className={styles.toolbar}>
            <Space>
              <Button
                icon={<FilterOutlined />}
                onClick={() => setShowFilters(!showFilters)}
                type={showFilters ? 'primary' : 'default'}
              >
                {t('common.filters', 'Filters')}
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={() => {
                  fetchDeletionLogs(pagination.current, pagination.pageSize);
                  calculateStatistics();
                }}
              >
                {t('common.refresh', 'Refresh')}
              </Button>
              <Popconfirm
                title={t('admin.deletedFiles.bulkDeleteConfirm.title', 'Delete selected logs?')}
                description={t('admin.deletedFiles.bulkDeleteConfirm.description', 'This will delete {{count}} selected log(s). This action cannot be undone.', { count: selectedRowKeys.length })}
                onConfirm={handleBulkDelete}
                okText={t('common.delete', 'Delete')}
                cancelText={t('common.cancel', 'Cancel')}
                okButtonProps={{ danger: true }}
                disabled={selectedRowKeys.length === 0}
              >
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  disabled={selectedRowKeys.length === 0}
                  loading={deleteLoading}
                >
                  {t('admin.deletedFiles.bulkDelete', 'Delete Bulk')}
                  {selectedRowKeys.length > 0 && ` (${selectedRowKeys.length})`}
                </Button>
              </Popconfirm>
            </Space>
          </div>

          {/* Filters */}
          {showFilters && renderFilterPanel()}

          {/* Table */}
          <Table
            columns={columns}
            dataSource={deletionLogs}
            rowKey="id"
            rowSelection={rowSelection}
            loading={loading || deleteLoading}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              showTotal: (total, range) =>
                t('common.pagination', '{{start}}-{{end}} of {{total}} items', {
                  start: range[0],
                  end: range[1],
                  total,
                }),
            }}
            onChange={handleTableChange}
            scroll={{ x: 900 }}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={t('admin.deletedFiles.noLogs', 'No deletion logs found')}
                />
              ),
            }}
          />

        {/* Detail Modal */}
        {renderDetailModal()}
      </div>
    </PagesWrapper>
  );
};

export default DeletedFiles;
